/**
 * Custom hook for managing modal state.
 *
 * Eliminates the repeated pattern of:
 *   const [isOpen, setIsOpen] = useState(false);
 *   const [selectedItem, setSelectedItem] = useState<Type | null>(null);
 *   const handleOpen = (item: Type) => { setSelectedItem(item); setIsOpen(true); }
 *   const handleClose = () => { setIsOpen(false); setSelectedItem(null); }
 *
 * Usage:
 *   const modal = useModal<Supplier>();
 *
 *   // Open with data:
 *   <Button onClick={() => modal.open(supplier)}>View</Button>
 *
 *   // In modal:
 *   <Modal open={modal.isOpen} onClose={modal.close}>
 *     {modal.data && <SupplierDetails supplier={modal.data} />}
 *   </Modal>
 */

import { useState, useCallback } from 'react';

export interface ModalState<T = undefined> {
  isOpen: boolean;
  data: T | null;

  /** Open modal, optionally with data */
  open: (data?: T) => void;
  /** Close modal and clear data */
  close: () => void;
  /** Toggle modal open/closed */
  toggle: () => void;
  /** Update data without changing open state */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

/**
 * Manages open/close state and optional associated data for a single modal.
 * Replaces the common `[isOpen, setIsOpen] + [selectedItem, setSelectedItem]` pattern.
 *
 * @param initialData - Optional initial data to pre-populate the modal
 */
export function useModal<T = undefined>(initialData: T | null = null): ModalState<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<T | null>(initialData);

  const open = useCallback((newData?: T) => {
    if (newData !== undefined) {
      setData(newData);
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  return {
    isOpen,
    data,
    open,
    close,
    toggle,
    setData,
  };
}

/**
 * Hook for managing multiple related modals
 * Useful when you have view/edit/delete modals for the same entity
 */
export interface MultiModalState<T> {
  activeModal: string | null;
  data: T | null;

  /** Open a specific modal by name */
  open: (modalName: string, data?: T) => void;
  /** Close all modals */
  close: () => void;
  /** Check if a specific modal is open */
  isOpen: (modalName: string) => boolean;
  /** Update data */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

/**
 * Manages state for multiple named modals sharing the same data type.
 * Only one modal can be active at a time; opening one closes the previous.
 */
export function useMultiModal<T = undefined>(): MultiModalState<T> {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [data, setData] = useState<T | null>(null);

  const open = useCallback((modalName: string, newData?: T) => {
    if (newData !== undefined) {
      setData(newData);
    }
    setActiveModal(modalName);
  }, []);

  const close = useCallback(() => {
    setActiveModal(null);
    setData(null);
  }, []);

  const isOpen = useCallback((modalName: string) => {
    return activeModal === modalName;
  }, [activeModal]);

  return {
    activeModal,
    data,
    open,
    close,
    isOpen,
    setData,
  };
}

/**
 * Hook for confirmation dialogs
 */
export interface ConfirmModalState<T = undefined> {
  isOpen: boolean;
  data: T | null;
  isLoading: boolean;

  /** Request confirmation for an action */
  confirm: (data?: T) => Promise<boolean>;
  /** Accept the confirmation */
  accept: () => void;
  /** Reject/cancel the confirmation */
  reject: () => void;
  /** Set loading state (for async operations) */
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Manages a promise-based confirmation dialog.
 * Call `await confirm(data)` to open the dialog and receive a boolean result
 * when the user accepts or rejects.
 */
export function useConfirmModal<T = undefined>(): ConfirmModalState<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback((newData?: T): Promise<boolean> => {
    if (newData !== undefined) {
      setData(newData);
    }
    setIsOpen(true);

    return new Promise((resolve) => {
      setResolver(() => resolve);
    });
  }, []);

  const accept = useCallback(() => {
    setIsOpen(false);
    resolver?.(true);
    setResolver(null);
    setData(null);
  }, [resolver]);

  const reject = useCallback(() => {
    setIsOpen(false);
    resolver?.(false);
    setResolver(null);
    setData(null);
  }, [resolver]);

  return {
    isOpen,
    data,
    isLoading,
    confirm,
    accept,
    reject,
    setLoading,
  };
}

export default useModal;
